import { z } from "zod";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { cn } from "@/shared/lib/utils";
import {
  ChartSkeleton,
  CHART_TOOLTIP,
  CHART_COLORS,
} from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const RadarChartSchema = z.object({
  dimensions: z.array(
    z.object({
      axis: z.string(),
      value: z.number(),
      fullMark: z.number(),
    }),
  ),
  label: z.string().optional(),
});

type RadarChartData = z.infer<typeof RadarChartSchema>;

/* ────────────────────── Component ────────────────────── */

export function RadarChartWidget({
  widget,
  className,
}: AgentWidgetProps<RadarChartData>) {
  const { dimensions, label } = widget.data;

  return (
    <div className={cn("w-full", className)}>
      {label && <p className="mb-3 text-[11px] text-text-tertiary">{label}</p>}
      <ResponsiveContainer width="100%" height={260}>
        <RadarChart cx="50%" cy="50%" outerRadius="70%" data={dimensions}>
          <PolarGrid stroke="hsl(var(--border))" />
          <PolarAngleAxis
            dataKey="axis"
            tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          />
          <PolarRadiusAxis
            angle={30}
            domain={[0, "auto"]}
            tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
            tickCount={4}
          />
          <Radar
            name={label ?? "점수"}
            dataKey="value"
            stroke={CHART_COLORS[0]}
            fill={CHART_COLORS[0]}
            fillOpacity={0.25}
            strokeWidth={2}
          />
          <Tooltip
            {...CHART_TOOLTIP}
            formatter={(value: number) => [value, label ?? "점수"]}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function RadarChartSkeleton({ className }: { className?: string }) {
  return <ChartSkeleton height={280} className={className} />;
}

/* ────────────────────── Widget Definition ────────────────────── */

const radarChartWidget = defineWidget<RadarChartData>({
  type: WIDGET_TYPES.RADAR_CHART,
  label: "RadarChart (4D 스코어)",
  schema: RadarChartSchema,
  component: RadarChartWidget,
  skeleton: RadarChartSkeleton,
  defaultLayout: "half",
  maxHeight: 300,
  aiHint: "키워드/채널의 다차원 점수를 방사형 차트로 시각화",
});

registerWidget(radarChartWidget);
